/**
 *
 * @module eventBuffer
 *
 */

import Check from 'check-types';
import Services from './../../services/entry';
import Token from './../../token/entry';
import TelemetryEvent from './telemetryEvent';

const TelemetryClient = Services.Internal.Telemetry.TelemetryClient;
const TokenManager = Token.TokenManager;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    nonEmptyObject,
    instanceStrict
} = Check.assert;

/**
 *
 * @since 3.1.0
 * @class EventBuffer
 * @desc Provides a base class used for publishing and queueing Telemetry Events via a service client
 *
 */
export default class EventBuffer {

    constructor(bufferConfiguration, tokenManager, telemetryClientEndpoint, telemetryClient, logger) {

        const errorMessage = {
            ctor: 'EventBuffer(bufferConfiguration, tokenManager, telemetryClientEndpoint, telemetryClient, logger)',
            get tokenManager() {
                return `${this.ctor} needs TokenManager instance`;
            },
            get bufferConfiguration() {
                return `${this.ctor} needs BufferConfiguration object`;
            },
            get telemetryClient() {
                return `${this.ctor} needs TelemetryClient instance`;
            }
        };

        nonEmptyObject(bufferConfiguration, errorMessage.bufferConfiguration, InvalidArgumentException);
        instanceStrict(tokenManager, TokenManager, errorMessage.tokenManager, InvalidArgumentException);
        instanceStrict(telemetryClient, TelemetryClient, errorMessage.telemetryClient, InvalidArgumentException);

        /**
         *
         * @access private
         * @type {SDK.Services.Configuration.TelemetryManagerConfiguration.BufferConfiguration}
         *
         */
        this.config = bufferConfiguration;

        /**
         *
         * @access private
         * @type {Object}
         *
         */
        this.endpoint = telemetryClientEndpoint;

        /**
         *
         * @access private
         * @type {string}
         *
         */
        this.postMethod = this.endpoint.rel === 'postEvent' ? 'postEvents' : 'postDustEvents';

        /**
         *
         * @access private
         * @type {SDK.Token.Tokenmanager}
         *
         */
        this.tokenManager = tokenManager;

        /**
         *
         * @access private
         * @type {SDK.Services.Internal.Telemetry.TelemetryClient}
         *
         */
        this.client = telemetryClient;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {Boolean}
         *
         */
        this.isScheduled = false;

        /**
         *
         * @access private
         * @type {Number}
         *
         */
        this.batchLimit = this.config.batchLimit;

        /**
         *
         * @access private
         * @type {Number}
         *
         */
        this.replyAfter = null;

        /**
         *
         * @access private
         * @type {Number}
         *
         */
        this.minimumBatchSize = this.config.minimumBatchSize;

        /**
         *
         * @access private
         * @type {Number}
         *
         */
        this.replyAfterFallback = this.config.replyAfterFallback;

        /**
         *
         * @access private
         * @type {Boolean}
         *
         */
        this.initialPostEvent = true;

        /**
         *
         * @access private
         * @type {Array<Object>}
         * @desc The manager will persist telemetry events in a queue prior to
         * forwarding to the client. This queue should survive an app restart.
         * this queue should be specific to this instances type - stream events or dust events
         * @todo set in Storage
         * @todo drain queue on deauth
         *
         */
        this.queue = [];

        /**
         * @access private
         * @type {Boolean}
         * @desc if this is true then telemetry events will not be published
         */
        this.disabled = this.config.disabled;

        this.logger.log(EventBuffer.name, 'Created.');

    }

    /**
     *
     * @access public
     * @param {TelemetryEvent} telemetryEvent
     * @desc Post an event to the telemetry queue for upload to the ce-telemetry-service
     * @throws {InvalidArgumentException}
     * @returns {Promise<Void>}
     *
     */
    postEvent(telemetryEvent) {

        const { disabled, logger } = this;

        const errorMessage = {
            method: 'EventBuffer.postEvent(telemetryEvent)',
            get telemetryEvent() {
                return `${this.method} needs TelemetryEvent instance`;
            }
        };

        if (disabled) {
            logger.warn(EventBuffer.name, 'Buffering is disabled, events will not be posted.');
            return Promise.resolve();
        }

        Check.assert(Check.like(telemetryEvent, TelemetryEvent, errorMessage.telemetryEvent, InvalidArgumentException));

        return telemetryEvent.getPayload().then((telemetryPayload) => {

            logger.info(EventBuffer.name, 'Event added to queue.');

            this.queue.push(telemetryPayload);

            const queueLength = this.queue.length;

            if (this.initialPostEvent || queueLength >= this.minimumBatchSize || queueLength === this.batchLimit) {

                this.initialPostEvent = false;

                return this.processBatch();

            } else {

                this.scheduleNextBatch(this.replyAfter || this.replyAfterFallback);

                return Promise.resolve();

            }

        });

    }

    /* #region private: */

    /**
     *
     * @access private
     * @desc Grabs a fresh AccessToken from the AccessTokenProvider instance
     * @returns {SDK.Token.AccessToken}
     *
     */
    get accessToken() {

        return this.tokenManager.getAccessToken();

    }

    /**
     *
     * @access private
     * @returns {Promise<Void>}
     * @note client returns {TelemetryResponse} with replyAfter and requestId
     * @note the config has a replyAfterFallback prop in case replyAfter returns null
     *
     */
    processBatch() {

        const { logger, client, accessToken } = this;

        if (!this.queue.length) {

            logger.info(EventBuffer.name, `No buffer events to post. Count: ${this.queue.length}`);

            this.clearSchedule();

            return Promise.resolve();

        }

        logger.info(EventBuffer.name, 'Attempting to post events.');

        const telemetryPayloads = this.queue.slice(0, this.batchLimit);

        // Clear the queue up to the batch limit
        this.queue.splice(0, this.batchLimit);

        // this.postMethod will either be postEvents or postDustEvents
        return client[this.postMethod](accessToken, telemetryPayloads).then((telemetryResponse) => {

            this.replyAfter = telemetryResponse.replyAfter || this.replyAfterFallback;

            this.clearSchedule();
            this.scheduleNextBatch(this.replyAfter);

        }).catch((ex) => {

            // Add payloads back to the front of the queue on error.
            this.queue = telemetryPayloads.concat(this.queue);

            return Promise.reject(ex);

        });

    }

    /**
     *
     * @access private
     * @param {Number} replyAfter
     * @desc The client may return a replyAfter value in its response. If so,
     * the manager should note the time and not send any additional
     * events until after the specified wait period. If the client fails, the manager
     * should retry at a later time using thereplyAfter value if available, or a configured value
     * if the replyAfter value is not available.
     * @note If the application restarts, the telemetry manager should send any queued events.
     * @returns {Void}
     *
     */
    scheduleNextBatch(replyAfter) {

        // check for a current replyAfter timer, if already set then bail
        if (this.isScheduled) {
            return;
        }

        this.isScheduled = true;

        setTimeout(this.processBatch.bind(this), replyAfter * 1000);

    }

    /**
     *
     * @access private
     * @desc Clears the last scheduled batch
     *
     */
    clearSchedule() {

        this.isScheduled = false;

    }

}



// WEBPACK FOOTER //
// ./src/internal/telemetry/eventBuffer.js